package analytics

import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.add
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonArray
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

@OptIn(ExperimentalUuidApi::class, ExperimentalTime::class)
internal class PostHogAnalytics(
    private val config: AnalyticsConfig,
    private val preferences: AnalyticsPreferences,
    http: AnalyticsHttpEngine,
    private val logger: AnalyticsLogger,
) : Analytics {

    private val client = http.client
    private val json = Json { ignoreUnknownKeys = true }
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val mutex = Mutex()
    private val queue = ArrayDeque<JsonObject>()
    private val sessionId = Uuid.random().toString()
    private val superProperties = config.defaultProperties.toMutableMap()

    private var optedOut: Boolean = preferences.getString(KEY_OPTED_OUT) == "true"
    private var identifiedId: String? = preferences.getString(KEY_IDENTIFIED_ID)
    private var distinctId: String = identifiedId
        ?: preferences.getString(KEY_DISTINCT_ID)
        ?: Uuid.random().toString().also { preferences.putString(KEY_DISTINCT_ID, it) }

    init {
        scope.launch {
            while (isActive) {
                delay(config.flushIntervalMs.coerceAtLeast(1_000))
                flushQueue()
            }
        }
    }

    override fun screen(name: String, properties: Map<String, Any?>) {
        enqueue(
            event = EVENT_SCREEN,
            properties = properties + ("$SCREEN_NAME" to name),
        )
    }

    override fun track(event: AnalyticsEvent) {
        enqueue(event.name, event.properties)
    }

    override fun identify(userId: String, properties: Map<String, Any?>) {
        if (userId.isBlank()) return
        scope.launch {
            mutex.withLock {
                val previous = distinctId
                if (previous != userId) {
                    enqueueLocked(
                        event = EVENT_IDENTIFY,
                        properties = mapOf(
                            "\$anon_distinct_id" to previous,
                        ),
                        distinctIdOverride = userId,
                        personProperties = properties,
                    )
                    distinctId = userId
                    identifiedId = userId
                    preferences.putString(KEY_IDENTIFIED_ID, userId)
                    preferences.putString(KEY_DISTINCT_ID, userId)
                } else if (properties.isNotEmpty()) {
                    enqueueLocked(
                        event = EVENT_SET,
                        properties = emptyMap(),
                        personProperties = properties,
                    )
                }
            }
            flushQueue()
        }
    }

    override fun setPersonProperties(properties: Map<String, Any?>) {
        if (properties.isEmpty()) return
        enqueue(EVENT_SET, emptyMap(), personProperties = properties)
    }

    override fun register(key: String, value: Any?) {
        if (value == null) {
            superProperties.remove(key)
        } else {
            superProperties[key] = value
        }
    }

    override fun unregister(key: String) {
        superProperties.remove(key)
    }

    override fun optOut(optedOut: Boolean) {
        this.optedOut = optedOut
        preferences.putString(KEY_OPTED_OUT, optedOut.toString())
        if (optedOut) {
            scope.launch {
                mutex.withLock { queue.clear() }
            }
        }
    }

    override fun reset() {
        scope.launch {
            mutex.withLock {
                val next = Uuid.random().toString()
                distinctId = next
                identifiedId = null
                preferences.putString(KEY_DISTINCT_ID, next)
                preferences.remove(KEY_IDENTIFIED_ID)
            }
        }
    }

    override fun flush() {
        scope.launch { flushQueue() }
    }

    private fun enqueue(
        event: String,
        properties: Map<String, Any?>,
        personProperties: Map<String, Any?> = emptyMap(),
        distinctIdOverride: String? = null,
    ) {
        scope.launch {
            val shouldFlush = mutex.withLock {
                enqueueLocked(event, properties, personProperties, distinctIdOverride)
                queue.size >= config.batchSize
            }
            if (shouldFlush) flushQueue()
        }
    }

    private fun enqueueLocked(
        event: String,
        properties: Map<String, Any?>,
        personProperties: Map<String, Any?> = emptyMap(),
        distinctIdOverride: String? = null,
    ) {
        if (optedOut) return
        val payload = buildEvent(
            event = event,
            properties = properties,
            personProperties = personProperties,
            distinctIdOverride = distinctIdOverride,
        )
        queue.addLast(payload)
        if (config.debug) {
            logger.d("queued $event id=${distinctIdOverride ?: distinctId} $properties")
        }
    }

    private fun buildEvent(
        event: String,
        properties: Map<String, Any?>,
        personProperties: Map<String, Any?>,
        distinctIdOverride: String?,
    ): JsonObject {
        val merged = buildMap<String, Any?> {
            put("\$lib", LIB_NAME)
            put("\$lib_version", LIB_VERSION)
            put("\$session_id", sessionId)
            putAll(superProperties)
            putAll(properties.filterValues { it != null })
            if (personProperties.isNotEmpty()) {
                put("\$set", personProperties.filterValues { it != null })
            }
            if (preferences.getString(KEY_FIRST_SEEN) == null) {
                preferences.putString(KEY_FIRST_SEEN, Clock.System.now().toString())
                put(
                    "\$set_once",
                    mapOf(
                        "initial_platform" to superProperties["platform"],
                        "initial_app_version" to superProperties["app_version"],
                        "initial_app_name" to config.appName,
                    ),
                )
            }
        }

        return buildJsonObject {
            put("event", event)
            put("distinct_id", distinctIdOverride ?: distinctId)
            put("timestamp", Clock.System.now().toString())
            put("properties", merged.toJsonObject())
        }
    }

    private suspend fun flushQueue() {
        val batch = mutex.withLock {
            if (queue.isEmpty()) return
            buildList {
                repeat(minOf(queue.size, config.batchSize.coerceAtLeast(1))) {
                    add(queue.removeFirst())
                }
            }
        }
        val body = buildJsonObject {
            put("api_key", config.apiKey)
            putJsonArray("batch") {
                batch.forEach { add(it) }
            }
        }
        val url = config.host.trimEnd('/') + "/batch/"
        try {
            val response = client.post(url) {
                contentType(ContentType.Application.Json)
                setBody(json.encodeToString(JsonObject.serializer(), body))
            }
            if (!response.status.isSuccess()) {
                logger.e("PostHog flush failed: HTTP ${response.status}")
                mutex.withLock {
                    batch.asReversed().forEach { queue.addFirst(it) }
                }
            } else if (config.debug) {
                logger.d("flushed ${batch.size} events")
            }
        } catch (error: Throwable) {
            logger.e("PostHog flush failed", error)
            mutex.withLock {
                batch.asReversed().forEach { queue.addFirst(it) }
            }
        }
    }

    private companion object {
        const val LIB_NAME = "analytics-kmp"
        const val LIB_VERSION = "1.0.0"
        const val EVENT_SCREEN = "\$screen"
        const val EVENT_IDENTIFY = "\$identify"
        const val EVENT_SET = "\$set"
        const val SCREEN_NAME = "\$screen_name"
        const val KEY_DISTINCT_ID = "analytics.distinct_id"
        const val KEY_IDENTIFIED_ID = "analytics.identified_id"
        const val KEY_OPTED_OUT = "analytics.opted_out"
        const val KEY_FIRST_SEEN = "analytics.first_seen"
    }
}
